// The action bar: one button (a trigger) per kind of action in the action phase, whatever card it is done with. A
// trigger is switched on, then its target picked where it is (a card on the board, in hand, the wonder being built), and
// the action is done at once. Triggers with nothing to pick (Population) act at once.
// The options come from the action categories (see ActionCategories.js), so a trigger offers exactly what the action
// panel does; key(move) says where each is picked: 'tech:<name>', 'card:<id>', 'wonder', or 'tactic:<name>' for a
// tactic in play (by its card's id when it has gained strength, Sun Tzu's: it is not like the others of its name).
// steps: picked in two clicks, where from then where to (Upgrade).
// probe(name, from): the move a board card would make, to say why it cannot be picked.
// pick: what is picked once it is switched on, and where ("Then pick …").
// hand(player): the cards in hand it could be used with, each shown greyed out with its reason when it can't be.
// choose(player): how the choice between a target's several ways is put ({ title, label(option) }), when not as the
// action panel lists them.
// blocked(player, gameState): why it can't be used, asking in the order agreed for it (actions first: without one,
// nothing else matters), or null when none of these is why (the rules' own reason is given then).
// direct: its one target is not picked (there is only one wonder being built): it is done at once, or its ways asked
// about (the stages, a rival's International Red Cross); target: that target, given with the trigger.
// on: a card's own ability, with no button: switched on by clicking the card ('leader', or 'built:<wonder>' for a
// completed wonder), and only there while the player can use it.
// phase: the phase it is used in (the action phase unless it says), the others' triggers greyed out meanwhile.
// ask: the card picked, what comes next is always asked, whatever the ways to do it ('prepare': confirmed with the card
// shown; 'aggression', 'war', 'pact': the rival chosen with their strength in view). Its cards keep their own hover (the
// full card) while picked, with no popup by the mouse. askIf(move): asked only about those moves (a pact cancelled is
// done at once: it can be undone).
// inPlay(player, gameState): cards in play it is used on, clicked as cards in hand are (a pact, cancelled).
// way: what it does with a card, when a card clicked in hand could be used more than one way and which is asked
// (Confucius: an aggression, a war or a pact can be prepared as an event too).
// cost(card): what a card of it costs, shown on it in hand (the military actions printed on an aggression or a war;
// a rival's cards can make it more, as the rival picker shows).
const GameState = require('./entities/GameState');
const Rules = require('./Rules');
const { GetStats, StatsDelta } = require('./Utilities/Stats');
const { Phase, MoveTypes } = require('./Moves/MoveTypes');
const { CivilCardTypes, MilitaryCardTypes } = require('./Constants/Names/CardTypes');
const TechnologyTypes = require('./Constants/TechnologyTypes');
const { AgeOrder } = require('./Constants/Constants');
const WonderNames = require('./Constants/Names/WonderNames');
const { GetPopulationCost, GetBuildCost, GetUpgradeCost, GetDevelopCost, GetGovernmentCost, GetWonderStagesCost,
    WonderCurrency } = require('./Moves/Costs');

const tech = (name) => `tech:${name}`;
const card = (move) => `card:${move.cardId}`;

const NoCivil = 'No civil actions left';
const NoMilitary = 'No military actions left';
const civilLeft = (player) => player.GetCivilActionsRemaining() > 0;
const militaryLeft = (player, count = 1) => player.GetMilitaryActionsRemaining() >= count;
// (the pool choices the costs are worked out with, as the action panel's)
const choiceOf = (player) => player.poolChoice ?? { decline: false, trade: false };
const resources = (count) => `${count} resource${count === 1 ? '' : 's'}`;
const cheapest = (items) => items.reduce((best, item) => !best || item.pay < best.pay ? item : best, null);

const isMilitaryType = (type) => Object.values(TechnologyTypes.Military).includes(type);
const isUrbanType = (type) => Object.values(TechnologyTypes.Urban).includes(type);
const isBuildable = (t) => t.workerCost !== null && t.workerCost !== undefined;
// A kind of urban building the government allows no more of
const atUrbanLimit = (player, t) => isUrbanType(t.type) && player.technologies.filter(other => other.type === t.type)
    .reduce((sum, other) => sum + other.GetWorkerCount(), 0) >= player.GetUrbanBuildingLimit();
// Every move of a built card to a higher one of its kind on the board
const upgradePairs = (player) => player.technologies
    .filter(from => isBuildable(from) && from.GetWorkerCount() > 0)
    .flatMap(from => player.technologies
        .filter(to => isBuildable(to) && to.type === from.type && AgeOrder.indexOf(to.age) > AgeOrder.indexOf(from.age))
        .map(to => ({ from, to })));
// Of cards needing civil or military actions by kind: no action for any of them, as said for the kind there is
const noActionFor = (items, isMilitary) => items.some(item => !isMilitary(item)) ? NoCivil : NoMilitary;

const Triggers = [
    { id: 'population', label: 'Population', categories: ['increasePopulation'],
        // Actions, then food, then the yellow bank (no token in it: no food cost to name)
        blocked: (player) => {
            if (!civilLeft(player)) return NoCivil;
            const cost = GetPopulationCost(player, choiceOf(player));
            if (cost === null) return 'Your yellow bank is empty';
            return player.GetFood() < cost.pay ? `Needs ${cost.pay} food (you have ${player.GetFood()})` : null;
        } },
    { id: 'build', label: 'Build', categories: ['build', 'buildMilitary'], key: move => tech(move.techName),
        probe: name => ({ type: MoveTypes.Build, techName: name }), pick: 'what to build, on your board',
        // Neither action, then no worker to build with, then for each kind (buildings, units) the action for it and the
        // cheapest card of it. The urban limit never stops building as such (a farm or mine is always allowed), so it
        // only leaves out the urban buildings it stops when looking for the cheapest.
        blocked: (player) => {
            const civil = civilLeft(player), military = militaryLeft(player);
            if (!civil && !military) return 'No civil or military actions left';
            if (player.workerPool < 1) return 'No unused workers';
            const lines = [];
            const kind = (label, ofKind, hasAction, noAction) => {
                if (!hasAction)
                {
                    lines.push(`${label}: ${noAction.toLowerCase()}`);
                    return;
                }
                const best = cheapest(player.technologies.filter(t => isBuildable(t) && ofKind(t.type) && !atUrbanLimit(player, t))
                    .map(t => ({ name: t.name, pay: GetBuildCost(player, t, choiceOf(player)).pay })));
                if (best && player.GetResources() < best.pay)
                {
                    lines.push(`${label}: cheapest is ${best.name}, ${resources(best.pay)} (you have ${player.GetResources()})`);
                }
            };
            kind('Buildings', type => !isMilitaryType(type), civil, NoCivil);
            kind('Units', isMilitaryType, military, NoMilitary);
            return lines.length > 0 ? lines.join('\n') : null;
        } },
    // (Bach's too: an urban building to a theater, his once a turn, as one more where to)
    { id: 'upgrade', label: 'Upgrade', categories: ['upgrade', 'upgradeMilitary', 'bachUpgrade'], key: move => tech(move.fromTech), steps: true,
        probe: (name, from) => from ? { type: MoveTypes.Upgrade, fromTech: from, toTech: name } : null,
        pick: 'what to upgrade on your board, then what to',
        // Actions, then an upgrade there is, then the cheapest of those there is the action for
        blocked: (player) => {
            const civil = civilLeft(player), military = militaryLeft(player);
            if (!civil && !military) return 'No civil or military actions left';
            const pairs = upgradePairs(player);
            if (pairs.length === 0) return 'Nothing to upgrade';
            const doable = pairs.filter(pair => isMilitaryType(pair.from.type) ? military : civil);
            if (doable.length === 0) return noActionFor(pairs, pair => isMilitaryType(pair.from.type));
            const best = cheapest(doable.map(pair => ({ ...pair, pay: GetUpgradeCost(player, pair.from, pair.to, choiceOf(player)).pay })));
            return player.GetResources() < best.pay
                ? `Cheapest upgrade: ${best.from.name} → ${best.to.name}, ${resources(best.pay)} (you have ${player.GetResources()})` : null;
        } },
    { id: 'destroy', label: 'Destroy', categories: ['destroy'], key: move => tech(move.techName),
        probe: name => ({ type: MoveTypes.Destroy, techName: name }), pick: 'what to destroy, on your board',
        // Only ever the actions, unless nothing is built
        blocked: (player) => {
            const civil = civilLeft(player), military = militaryLeft(player);
            if (!civil && !military) return 'No civil or military actions left';
            const built = player.technologies.filter(t => isBuildable(t) && t.GetWorkerCount() > 0);
            if (built.length === 0) return 'Nothing built to destroy';
            return built.some(t => isMilitaryType(t.type) ? military : civil) ? null : noActionFor(built, t => isMilitaryType(t.type));
        } },
    // (a government too: developing it is changing to it, peacefully or by a revolution)
    { id: 'develop', label: 'Develop', categories: ['developTechnology', 'changeGovernment'], key: card,
        pick: 'a technology or government in your hand',
        hand: (player) => player.civilHand.filter(c => c?.type === CivilCardTypes.Technology),
        // Actions, then something in hand to develop, then the science for the cheapest, then Scientific Cooperation
        blocked: (player, gameState) => {
            if (!civilLeft(player)) return NoCivil;
            const cards = player.civilHand.filter(c => c?.type === CivilCardTypes.Technology);
            const isGovernment = (c) => c.Technology.type === TechnologyTypes.Government;
            const developable = cards.filter(c => isGovernment(c) ? player.government !== c.name : !player.HasTechnology(c.name));
            if (developable.length === 0) return cards.length > 0 ? 'You already have every technology in your hand' : 'No technology in hand';
            // (a government: peacefully, or by a revolution while no civil action is spent)
            const cost = (c) => isGovernment(c)
                ? Math.min(GetGovernmentCost(player, c.Technology, false).total,
                    player.civilActionsUsed === 0 ? GetGovernmentCost(player, c.Technology, true).total : Infinity)
                : GetDevelopCost(player, c.Technology, choiceOf(player)).pay;
            const best = cheapest(developable.map(c => ({ name: c.name, pay: cost(c) })));
            if (player.science < best.pay) return `Cheapest is ${best.name}, ${best.pay} science (you have ${player.science})`;
            return require('./Moves/Pact').CooperationProblem(gameState, player);
        } },
    // (with a construction technology, e.g. Masonry, and enough for more than one stage: how many, asked; a rival's
    // International Red Cross is built here too: which wonder, asked)
    { id: 'wonder', label: 'Wonder', categories: ['buildWonder', 'redCross'], key: () => 'wonder', direct: true,
        choose: (player, options) => options.some(option => option.move.type === MoveTypes.RedCrossStage)
            ? { title: 'Which wonder?', label: (option) => option.move.type === MoveTypes.RedCrossStage ? option.label
                : `${player.wonderInProgress.card.name}: ${option.move.stages} stage${option.move.stages === 1 ? '' : 's'}` }
            : { title: `${player.wonderInProgress.card.name}: how many stages?`,
                label: (option) => `${option.move.stages} stage${option.move.stages === 1 ? '' : 's'}` },
        // Actions, then a wonder being built, then the next stage's cost
        blocked: (player) => {
            if (!civilLeft(player)) return NoCivil;
            const wonder = player.wonderInProgress;
            if (!wonder) return 'No wonder being built';
            const pay = GetWonderStagesCost(player, 1, choiceOf(player)).pay;
            const currency = WonderCurrency(wonder);
            const have = currency === 'food' ? player.GetFood() : player.GetResources();
            return have < pay ? `Next stage of ${wonder.card.name}: ${currency === 'food' ? `${pay} food` : resources(pay)} (you have ${have})` : null;
        } },
    { id: 'leader', label: 'Leader', categories: ['playLeader'], key: card, pick: 'a leader in your hand',
        hand: (player) => player.civilHand.filter(c => c?.type === CivilCardTypes.Leader),
        blocked: (player) => !civilLeft(player) ? NoCivil
            : !player.civilHand.some(c => c?.type === CivilCardTypes.Leader) ? 'No leader in hand' : null },
    // (inner: a card that comes with an action has it picked where it is, see InnerTargetsOf)
    { id: 'action', label: 'Action card', categories: ['playAction'], key: card, pick: 'an action card in your hand', inner: true,
        hand: (player) => player.civilHand.filter(c => c?.type === CivilCardTypes.Action),
        // Actions, then a card in hand (each card that can't be played says why when picking)
        blocked: (player) => !civilLeft(player) ? NoCivil
            : !player.civilHand.some(c => c?.type === CivilCardTypes.Action) ? 'No action card in hand' : null },
    // (from hand, or copying one in play)
    { id: 'tactic', label: 'Tactic', categories: ['playTactic', 'copyTactic'],
        key: move => move.type === MoveTypes.CopyTactic ? `tactic:${move.tacticCardId ?? move.tacticName}` : card(move),
        pick: 'a tactic in your hand, or one in play to copy',
        hand: (player) => player.militaryHand.filter(c => c?.type === MilitaryCardTypes.Tactic),
        // Military actions for either way (1 to play one, 2 to copy one), then one change of tactic a turn, then a tactic
        // to play or copy
        blocked: (player, gameState) => {
            if (!militaryLeft(player)) return NoMilitary;
            if (player.changedTacticThisTurn) return 'You can only play or copy one tactic per turn';
            const inHand = player.militaryHand.some(c => c?.type === MilitaryCardTypes.Tactic);
            const inPlay = gameState.publicTactics.some(c => c.name !== player.currentTactic || gameState.tacticBonuses?.[c.id]);
            if (!inHand && !inPlay) return 'No tactic in hand or in play';
            return !inHand && !militaryLeft(player, 2) ? 'Copying a tactic needs 2 military actions' : null;
        } },

    // The political phase: the military cards played as the political action, each picked in hand, then asked about
    { id: 'prepare', label: 'Prepare Event', categories: ['prepareEvent'], key: card, phase: Phase.Political, ask: 'prepare',
        pick: 'an event in your hand', way: 'Prepare it as an event',
        hand: (player) => player.militaryHand.filter(c => c?.type === MilitaryCardTypes.Event || c?.type === MilitaryCardTypes.Territory),
        blocked: (player) => player.militaryHand.length === 0 ? 'No military cards in hand' : null },
    { id: 'aggression', label: 'Aggression', categories: ['playAggression'], key: card, phase: Phase.Political, ask: 'aggression',
        pick: 'an aggression in your hand', way: 'Play it as an aggression', cost: (c) => `${c.aggression.cost} MA`,
        hand: (player) => player.militaryHand.filter(c => c?.type === MilitaryCardTypes.Aggression),
        blocked: (player) => !player.militaryHand.some(c => c?.type === MilitaryCardTypes.Aggression) ? 'No aggression in hand' : null },
    { id: 'war', label: 'War', categories: ['declareWar'], key: card, phase: Phase.Political, ask: 'war',
        pick: 'a war in your hand', way: 'Declare war with it', cost: (c) => `${c.war.cost} MA`,
        hand: (player) => player.militaryHand.filter(c => c?.type === MilitaryCardTypes.War),
        blocked: (player) => !player.militaryHand.some(c => c?.type === MilitaryCardTypes.War) ? 'No war in hand' : null },
    // (offering one from hand, or cancelling one in play)
    { id: 'pact', label: 'Pact', categories: ['offerPact', 'cancelPact'], key: card, phase: Phase.Political, ask: 'pact',
        askIf: (move) => move.type !== MoveTypes.CancelPact,
        pick: 'a pact in your hand, or one in play to cancel', way: 'Offer it as a pact',
        hand: (player) => player.militaryHand.filter(c => c?.type === MilitaryCardTypes.Pact),
        inPlay: (player, gameState) => require('./Moves/Pact').PactsOf(gameState, player.playerNumber).map(pact => pact.card),
        blocked: (player, gameState) => !player.militaryHand.some(c => c?.type === MilitaryCardTypes.Pact)
            && require('./Moves/Pact').PactsOf(gameState, player.playerNumber).length === 0 ? 'No pact in hand or in play' : null },

    // Cards' own abilities, used by clicking the card
    { id: 'louvre', label: 'Louvre Museum', categories: ['louvre'], on: `built:${WonderNames.LouvreMuseum}` },
    { id: 'oceanLiner', label: 'Ocean Liner', categories: ['oceanLiner'], on: `built:${WonderNames.OceanLiner}` },
    { id: 'churchill', label: 'Churchill', categories: ['churchill'], on: 'leader' },
    { id: 'barbarossa', label: 'Barbarossa', categories: ['barbarossa'], on: 'leader', key: move => tech(move.techName),
        probe: name => ({ type: MoveTypes.BarbarossaRecruit, techName: name }), pick: 'a unit to recruit, on your board' },
];

// A cost shown on a card with the actions using it spends first ("1 CA, 3 science"), unless it names its own (a
// revolution's "all CA", a leader's "1 CA")
const WithActions = (cost, actions) => /\b(CA|MA)\b/.test(cost ?? '') ? cost
    : [actions, ...(cost && cost !== 'free' ? [cost] : [])].join(', ');

// A leader's cost on its card in hand: just the action, without who it replaces ("replaces Eleanor of Aquitaine"; too
// long for the card, and undo puts it right), and Hammurabi's "replace with MA" as "1 MA"
const LeaderCost = (cost) => cost?.startsWith('replaces ') ? undefined
    : cost?.startsWith('replace with ') ? `1 ${cost.slice('replace with '.length)}` : cost;

// Why a trigger has nothing to pick, when neither its own checks nor the rules say why
const Nothing = 'Nothing to do with it now';

// The trigger's options, with the category's group (civil or military: which action it spends)
const OptionsOf = (trigger, actionOptions) => actionOptions.categories
    .filter(category => trigger.categories.includes(category.id))
    .flatMap(category => category.options.map(option => ({ ...option, group: category.group })));

// The bar as the player looking sees it: every trigger, whether it can be used and why not; and, for the one switched
// on (picking: { trigger, from?, choosing? }), the targets: { key: { options, selectable, reason?, mark? } }, with
// Upgrade's second step keyed by where to, and the cards in hand it could be used with but can't, each with why.
// choice: a target with several ways to do it (wonder stages, a revolution, an action card's uses), to choose between.
// hand, upgradable, wonderStages: with no trigger on, what clicking each card in hand would do, what can be upgraded, and
// the wonder's stages that can be built (see below).
// usable: the player looking may act now (their own action phase, nothing sent and waiting); gate: why no one may act
// now (a decision first), before any trigger's own reason; hidden: no triggers at all (the first round, when only cards
// are taken from the card row); whyNot(categoryIds, cardId?): what the rules say against the moves those categories
// could make (with that card), or null; costOf(categoryIds, cardId): what using that card would cost, when it can't be
// used; player and gameState: whose bar it is, for the triggers' own checks
const ActionBar = ({ actionOptions, picking, usable, phase, gate = null, hidden = false, whyNot = () => null, costOf = () => null,
    player, gameState }) => {
    if (hidden)
    {
        return { triggers: [], active: null, targets: {}, choice: null, hand: {}, upgradable: [], wonderStages: [] };
    }
    // (usable: in the player's own action or political phase; each trigger only in its own, phase: which it is)
    const ownPhase = (trigger) => (trigger.phase ?? Phase.Action) === phase;
    const triggers = Triggers.flatMap(trigger => {
        const now = usable && ownPhase(trigger);
        const options = OptionsOf(trigger, actionOptions);
        const available = options.filter(option => !option.disabled);
        // (a card's own ability only while it can be used, or could but for the actions)
        if (trigger.on && !(now && options.length > 0))
        {
            return [];
        }
        let reason;
        if (!now)
        {
            reason = ownPhase(trigger) ? 'Not your turn'
                : trigger.phase === Phase.Political ? 'In your political phase' : 'In your action phase';
        } else if (available.length === 0) {
            reason = gate ?? trigger.blocked?.(player, gameState) ?? options.find(option => option.reason)?.reason
                ?? whyNot(trigger.categories) ?? Nothing;
        }
        return [{ id: trigger.id, label: trigger.label, pick: trigger.pick, enabled: now && available.length > 0, reason,
            active: now && picking?.trigger === trigger.id, immediate: !trigger.key, on: trigger.on ?? null,
            phase: trigger.phase ?? Phase.Action, ask: trigger.ask ?? null,
            // (a direct one's target, to do, ask about or preview)
            ...(trigger.direct ? { direct: true, target: now && available.length > 0 ? TargetsOf(trigger, options, { trigger: trigger.id }, player, whyNot)[trigger.key(available[0].move)] ?? null : null } : {}),
            // (an immediate trigger's one option, to do and preview)
            ...(trigger.key ? {} : { option: available[0] ?? null }) }];
    });
    const active = triggers.find(trigger => trigger.active && trigger.enabled);
    if (!active || active.immediate)
    {
        // With no trigger on, a card in hand does what its trigger would with it, clicked (hand: { 'card:<id>': { trigger,
        // selectable, reason?, cost?, target } }, target as the trigger's own; cost: what doing it costs, shown on the
        // card, worked out by costOf when it can't be done); greyed-out triggers' cards say why the trigger is
        const hand = {};
        // and the board's cards that can be upgraded (upgradable: their names), each clicked to upgrade it
        let upgradable = [];
        if (usable)
        {
            triggers.filter(t => Triggers.find(def => def.id === t.id).hand && t.phase === phase).forEach(t => {
                const def = Triggers.find(d => d.id === t.id);
                const targets = TargetsOf(def, OptionsOf(def, actionOptions), { trigger: t.id }, player, whyNot);
                [...def.hand(player), ...(def.inPlay?.(player, gameState) ?? [])].forEach(c => {
                    const target = targets[`card:${c.id}`];
                    const found = target?.options.find(option => !option.disabled)?.cost ?? costOf(def.categories, c.id);
                    const cost = def.id === 'leader' ? LeaderCost(found) : found;
                    hand[`card:${c.id}`] = { trigger: t.id, target, selectable: t.enabled && !!target?.selectable,
                        // (the political action spends no action of its own: only what the card says, if anything)
                        reason: t.enabled ? target?.reason : t.reason,
                        cost: def.phase === Phase.Political ? def.cost?.(c) : WithActions(cost, def.id === 'tactic' ? '1 MA' : '1 CA') };
                });
                // (and a tactic in play, clicked to copy it)
                Object.entries(targets).filter(([key]) => key.startsWith('tactic:')).forEach(([key, target]) => {
                    hand[key] = { trigger: t.id, target, selectable: t.enabled && target.selectable,
                        reason: t.enabled ? target.reason : t.reason, cost: WithActions(target.options.find(option => !option.disabled)?.cost, '2 MA') };
                });
            });
            // Any military card that can be prepared (Confucius): one with a use of its own too asks which (ways), one
            // with none in this phase (a tactic, say) is prepared
            const prepare = Triggers.find(def => def.id === 'prepare');
            if (triggers.find(t => t.id === prepare.id)?.enabled)
            {
                Object.entries(TargetsOf(prepare, OptionsOf(prepare, actionOptions), { trigger: prepare.id }, player, whyNot))
                    .filter(([key, target]) => target.selectable && hand[key]?.trigger !== prepare.id)
                    .forEach(([key, target]) => {
                        hand[key] = hand[key]?.selectable ? { ...hand[key], ways: [hand[key].trigger, prepare.id] }
                            : { trigger: prepare.id, target, selectable: true };
                    });
            }
            const upgrade = Triggers.find(t => t.id === 'upgrade');
            if (triggers.find(t => t.id === upgrade.id)?.enabled)
            {
                const targets = TargetsOf(upgrade, OptionsOf(upgrade, actionOptions), { trigger: upgrade.id }, player, whyNot);
                upgradable = Object.keys(targets).filter(key => targets[key].selectable).map(key => key.slice('tech:'.length));
            }
        }
        // and the wonder being built, each stage it can be built up to now clicked to build up to it (wonderStages: the
        // number of stages for each, as many as Masonry and the like allow, with its move)
        const wonderStages = usable && phase === Phase.Action ? actionOptions.categories.filter(category => category.id === 'buildWonder')
            .flatMap(category => category.options.filter(option => !option.disabled)).map(option => ({ stages: option.move.stages, move: option.move })) : [];
        return { triggers, active: null, targets: {}, choice: null, hand, upgradable, wonderStages };
    }
    const trigger = Triggers.find(t => t.id === active.id);
    // An action card that comes with an action (Urban Growth, Breakthrough…) picked: what it is used for is picked where
    // it is (cardPick: { card, name, pick })
    const playing = trigger.inner && picking.card !== undefined && picking.card !== null
        ? OptionsOf(trigger, actionOptions).filter(option => option.move.cardId === picking.card) : null;
    const targets = playing ? InnerTargetsOf(playing, picking) : TargetsOf(trigger, OptionsOf(trigger, actionOptions), picking, player, whyNot);
    const cardPick = playing ? { card: picking.card, name: player.civilHand.find(c => c?.id === picking.card)?.name ?? trigger.label,
        pick: InnerPick(playing) } : null;
    // (a political card's cost stays on it while it is picked)
    if (trigger.cost)
    {
        trigger.hand(player).filter(c => targets[`card:${c.id}`]).forEach(c => { targets[`card:${c.id}`].cost = trigger.cost(c); });
    }
    if (playing)
    {
        // (the card itself, clicked again, puts it back down)
        targets[`card:${picking.card}`] = { options: [], selectable: true, mark: 'source' };
    }
    const choosing = picking.choosing && targets[picking.choosing];
    const put = choosing && !playing && trigger.choose?.(player, choosing.options);
    const choice = choosing ? { key: picking.choosing, title: put?.title ?? cardPick?.name ?? active.label,
        // (one asked about: every way, the rivals that can't be picked shown with why)
        options: choosing.options.filter(option => trigger.ask || !option.disabled).map(option => put ? { ...option, label: put.label(option) } : option) } : null;
    return { triggers, active: active.id, from: trigger.steps || playing ? picking.from ?? null : null, targets, choice, cardPick,
        steps: !!trigger.steps, hand: {}, upgradable: [], wonderStages: [] };
};

// The inner move of an action card's option: where it is picked. A build on the card built on; an upgrade first by the
// card upgraded from (then by where to); a technology or government developed by its card in hand; a wonder's stage
// on the wonder; more population on the population (no place on the board: the only option, done at once)
const InnerKey = (move) => move.type === MoveTypes.Build ? tech(move.techName)
    : move.type === MoveTypes.Upgrade ? tech(move.fromTech)
    : move.type === MoveTypes.DiscoverTechnology || move.type === MoveTypes.ChangeGovernment ? card(move)
    : move.type === MoveTypes.BuildWonderStage ? 'wonder'
    : 'population';

// What is picked for the action card being played, as in "Urban Growth: pick …"
const InnerPick = (options) => {
    const types = new Set(options.map(option => option.move.inner?.type));
    const board = [types.has(MoveTypes.Build) && 'build', types.has(MoveTypes.Upgrade) && 'upgrade'].filter(Boolean);
    return board.length > 0 ? `what to ${board.join(' or ')}, on your board`
        : types.has(MoveTypes.DiscoverTechnology) ? `a technology${types.has(MoveTypes.ChangeGovernment) ? ' or government' : ''} in your hand`
        : types.has(MoveTypes.ChangeGovernment) ? 'a government in your hand'
        : 'the wonder you are building';
};

// The targets of an action card being played: each of its options where its inner move is picked. An upgrade is picked
// in two clicks, where from then where to; where from, clicked again, builds on it when the card allows that too (Urban
// Growth, Rich Land), and otherwise takes it back. upgradeFrom: a card clicked to upgrade from it.
const InnerTargetsOf = (options, picking) => {
    const targets = {};
    const add = (key, option, extra = {}) => {
        const target = targets[key] ?? (targets[key] = { options: [], selectable: false });
        Object.assign(target, extra);
        target.options.push(option);
        if (!option.disabled)
        {
            target.selectable = true;
        } else {
            target.reason = target.reason ?? option.reason;
        }
    };
    if (picking.from)
    {
        options.filter(option => option.move.inner.type === MoveTypes.Upgrade && option.move.inner.fromTech === picking.from)
            .forEach(option => add(tech(option.move.inner.toTech), option));
        const builds = options.filter(option => option.move.inner.type === MoveTypes.Build && option.move.inner.techName === picking.from);
        targets[tech(picking.from)] = { options: [], selectable: true, mark: 'source' };
        builds.forEach(option => add(tech(picking.from), option, { mark: 'source' }));
        return targets;
    }
    options.forEach(option => {
        const key = InnerKey(option.move.inner);
        if (option.move.inner.type !== MoveTypes.Upgrade)
        {
            add(key, option);
            return;
        }
        // (an upgrade's where from holds no option of its own: the builds on it, if any, are done once it is picked again)
        const target = targets[key] ?? (targets[key] = { options: [], selectable: false });
        if (!option.disabled)
        {
            target.upgradeFrom = true;
            target.selectable = true;
        } else {
            target.reason = target.reason ?? option.reason;
        }
    });
    return targets;
};

// A trigger's targets while it is on (picking: { trigger, from? }): { key: { options, selectable, reason?, mark? } }
const TargetsOf = (trigger, options, picking, player, whyNot) => {
    const targets = {};
    const add = (key, option, mark) => {
        const target = targets[key] ?? (targets[key] = { options: [], selectable: false, mark });
        target.options.push(option);
        if (!option.disabled)
        {
            target.selectable = true;
        } else {
            target.reason = target.reason ?? option.reason;
        }
    };
    if (trigger.steps && picking.from)
    {
        // Where from is picked: where to, among its upgrades (where from again takes it back)
        options.filter(option => option.move.fromTech === picking.from).forEach(option => add(tech(option.move.toTech), option));
        targets[tech(picking.from)] = { options: [], selectable: true, mark: 'source', reason: undefined };
    } else {
        options.forEach(option => add(trigger.key(option.move), option));
    }
    // The cards in hand it could be used with but can't: greyed out, with the rules' reason
    (trigger.hand?.(player) ?? []).filter(c => !targets[`card:${c.id}`]).forEach(c => {
        targets[`card:${c.id}`] = { options: [], selectable: false, reason: whyNot(trigger.categories, c.id) ?? `Can't be used now` };
    });
    return targets;
};

// The board's buildings and units for the trigger on, as the board shows them: whether each can be picked (or why not:
// what the rules say about doing it with that card), and its target's key
const BoardCells = (bar, gameState, player) => {
    const trigger = Triggers.find(t => t.id === bar.active);
    const why = (name) => {
        const move = trigger.probe?.(name, bar.from);
        const error = move ? Rules.Validate(gameState, { ...move, player: player.playerNumber }) : null;
        return error ?? (bar.cardPick ? `Not with ${bar.cardPick.name}` : `Cannot be picked to ${trigger.label.toLowerCase()}`);
    };
    const cells = {};
    player.technologies.filter(t => t.workerCost !== null && t.workerCost !== undefined).forEach(t => {
        const target = bar.targets[tech(t.name)];
        cells[t.name] = target
            ? { selectable: target.selectable, reason: target.selectable ? undefined : target.reason, mark: target.mark, target: tech(t.name) }
            : { selectable: false, reason: why(t.name), target: tech(t.name) };
    });
    return cells;
};

// What a move would do for the player making it, for the popup by the mouse: what it costs (option.cost, the action
// panel's text, and the actions it spends) and what changes ([{ stat, label, change }]); null if it cannot be worked out
const STOCKS = ['resources', 'food', 'science'];
const PreviewMove = (gameState, move, option) => {
    try {
        const copy = new GameState(JSON.parse(JSON.stringify(gameState.toData())));
        const before = copy.playerStates[move.player];
        const stats = GetStats(before);
        // (the actions used, not those left: a new government's extra action is a gain, not one less spent)
        // (and no more than were left: a revolution marks all of the new government's as used)
        const used = { civil: before.civilActionsUsed, military: before.militaryActionsUsed };
        const left = { civil: before.GetCivilActionsRemaining(), military: before.GetMilitaryActionsRemaining() };
        const after = Rules.Apply(copy, move).playerStates[move.player];
        const spends = [];
        const civil = Math.min(after.civilActionsUsed - used.civil, left.civil);
        const military = Math.min(after.militaryActionsUsed - used.military, left.military);
        if (civil > 0) spends.push(`${civil} CA`);
        if (military > 0) spends.push(`${military} MA`);
        // (what is paid is the cost: what is left are the gains, and losses such as food eaten each turn)
        // (civil and military actions among the changes are the actions each turn, e.g. a new government's)
        // (nor the card played leaving the military hand)
        const changes = StatsDelta(stats, GetStats(after)).filter(change => !(STOCKS.includes(change.stat) && change.change < 0)
            && change.stat !== 'militaryCards');
        return { cost: option?.cost ?? null, costNote: option?.costNote ?? null, spends, changes };
    } catch (e) {
        return null;
    }
};

module.exports = { ActionBar, BoardCells, PreviewMove, Triggers, TechKey: tech };
